import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { get, post, isAuthenticated } from '../utils/api';
import { Card, Spin, Input, Button, List, Avatar, message, Upload, Image, Tooltip } from 'antd';
import { PictureOutlined, WechatOutlined, WeiboOutlined } from '@ant-design/icons';
import HelpPostMap from './HelpPostMap';
import MessageIcon from '@mui/icons-material/Message';
import ShareIcon from '@mui/icons-material/Share';
import QrCodeIcon from '@mui/icons-material/QrCode';
import './css/HelpPostDetail.css';

const { TextArea } = Input;

const HelpPostDetail = () => {
  // Function to share to social media
  const shareToSocialMedia = (platform) => {
    const currentUrl = window.location.href;
    const title = post ? post.title : '猫咪帮助';
    
    let shareUrl = '';
    
    switch(platform) {
      case 'wechat':
        // For WeChat, typically you'd show a QR code for the user to scan
        // Here we'll just alert the user to copy the link
        alert('请复制链接并在微信中分享: ' + currentUrl);
        return;
      case 'weibo':
        shareUrl = `http://service.weibo.com/share/share.php?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(title)}`;
        break;
      case 'qq':
        shareUrl = `http://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(title)}`;
        break;
      default:
        shareUrl = currentUrl;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=500');
  };
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [fileList, setFileList] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchPost = async () => {
    try {
      // Don't require authentication for viewing post details
      const data = await get(`/api/help-posts/${id}/`);
      console.log('Fetched post:', data);
      setPost(data);
      fetchComments(); // Fetch the comments after fetching the post
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      // Don't require authentication for viewing comments
      const data = await get(`/api/help-posts/${id}/comments/list/`);
      console.log('Fetched comments:', data);
      setComments(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [id]);

  const getPostTypeName = (postType) => {
    const types = {
      'lost': '猫咪丢失',
      'found': '发现猫咪',
      'adoption': '领养',
      'rescue': '救助',
      'other': '其它'
    };
    return types[postType] || postType;
  };

  const handleNewCommentChange = (e) => {
    setNewComment(e.target.value);
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() && fileList.length === 0) {
      message.error('请输入回复内容或上传图片');
      return;
    }

    try {
      // Create FormData to handle file uploads
      const formData = new FormData();
      formData.append('content', newComment);
      
      // Append each file to the FormData
      fileList.forEach((file, index) => {
        formData.append(`uploaded_images`, file.originFileObj);
      });
      
      // Use axios directly for FormData since our utility doesn't handle it
      const token = localStorage.getItem('token'); // For backward compatibility
      
      // Don't set Content-Type header for FormData, let the browser set it with the boundary
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      // We can't use our utility function here because we need to send FormData
      const response = await fetch(`${API_BASE_URL}/api/help-posts/${id}/comments/create/`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include' // Include cookies
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('New comment submitted:', data);
      
      // After submitting, fetch the comments again to get the updated data with images
      await fetchComments();
      
      setNewComment('');
      setFileList([]);
      message.success('回复已成功提交');
    } catch (error) {
      console.error('Failed to submit comment:', error);
      message.error('提交回复失败: ' + (error.message || '未知错误'));
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB!');
    }
    return isImage && isLt5M;
  };

  if (loading) {
    return <Spin size="large" />;
  }

  if (!post) {
    return <div>Post not found</div>;
  }

  return (
    <div className="help-post-detail">
      <div className="help-post-card-container">
        <div className="custom-card-header">
          <Link to="/help-post">
            <Button type="primary" className="return-button"> ⏎ 返回列表</Button>
          </Link>
          <div className="card-title">{post.title}</div>
          <div className="social-share-buttons">
            <Button 
              icon={<WechatOutlined />} 
              shape="circle" 
              onClick={() => shareToSocialMedia('wechat')}
              title="分享到微信"
              style={{ marginRight: '8px' }}
            />
            <Button 
              icon={<WeiboOutlined />} 
              shape="circle" 
              onClick={() => shareToSocialMedia('weibo')}
              title="分享到微博"
              style={{ marginRight: '8px' }}
            />
            <Button 
              icon={<QrCodeIcon />} 
              shape="circle" 
              onClick={() => shareToSocialMedia('qq')}
              title="分享到QQ"
              style={{ marginRight: '8px' }}
            />
            <Button 
              icon={<ShareIcon />} 
              shape="circle" 
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: post.title,
                    url: window.location.href
                  });
                } else {
                  alert('复制链接分享: ' + window.location.href);
                }
              }}
              title="其他分享"
            />
          </div>
        </div>
        
        <div className="help-post-card">
          <div className="post-content-wrapper">
            {/* Unified layout with map and details */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
              {/* Left side - Map */}
              {(post.latitude && post.longitude) && (
                <div style={{ flex: '1', height: '500px', minWidth: '300px' }}>
                  <HelpPostMap 
                    helpPosts={[{
                      ...post,
                      // Ensure these are explicitly set as numbers for the map component
                      latitude: parseFloat(post.latitude),
                      longitude: parseFloat(post.longitude)
                    }]}
                    showInfoWindow={false} 
                  />
                </div>
              )}
              
              {/* Right side - Post details */}
              <div style={{ flex: '1', minWidth: '300px' }}>
                <div className="info-row">
                  <div className="info-item">
                    <strong>类型：</strong>{getPostTypeName(post.post_type)}
                  </div>
                  <div className="info-item" style={{ display: 'flex', alignItems: 'center' }}>
                    <strong>发帖人：</strong>{post.creator.username}
                    {isAuthenticated() && localStorage.getItem('username') !== post.creator.username && (
                    <Tooltip title="私信">
                      <MessageIcon style={{ marginLeft: '10px', cursor: 'pointer' }} onClick={() => navigate(`/messages/${post.creator.id}`)} />
                    </Tooltip>
                    )}
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-item">
                    <strong>发帖时间：</strong>{new Date(post.created_at).toLocaleString('zh-CN')}
                  </div>
                  {(post.province || post.city || post.district) ? (
                    <div className="info-item">
                      <strong>位置：</strong>{post.province || ''} {post.city || ''} {post.district || ''}
                    </div>
                  ) : (post.latitude && post.longitude) && (
                    <div className="info-item">
                      <strong>位置：</strong>纬度 {post.latitude.toFixed(6)}, 经度 {post.longitude.toFixed(6)}
                    </div>
                  )}
                </div>
                
                {post.cat && (
                  <div className="info-row">
                    <div className="info-item">
                      <strong>相关猫咪：</strong>{post.cat.name}
                    </div>
                  </div>
                )}
                
                <div className="description-section">
                  <strong>描述：</strong>
                  <p>{post.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card title="回复" className="helppost-comments-card">
        {comments.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={comments}
            renderItem={comment => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Link to={`/user-profile/${comment.creator.id}`}>
                      <Avatar src={comment.creator.profile_picture_url || `https://xsgames.co/randomusers/avatar.php?g=pixel&key=${comment.creator.id}`} />
                    </Link>
                  }
                  title={<Link to={`/user-profile/${comment.creator.id}`}>{comment.creator.username}</Link>}
                  description={
                    <div>
                      <div>{comment.content}</div>
                      {comment.images && comment.images.length > 0 && (
                        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {comment.images.map((img, index) => (
                            <Image
                              key={index}
                              src={img.image_url || `${API_BASE_URL}${img.image}`}
                              alt={`Comment image ${index + 1}`}
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              preview={{ src: img.image_url || `${API_BASE_URL}${img.image}` }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  }
                />
                <div>{new Date(comment.created_at).toLocaleString('zh-CN')}</div>
              </List.Item>
            )}
          />
        ) : (
          <p>暂无回复</p>
        )}
      </Card>

      {isAuthenticated() ? (
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>添加回复</span>
              <Upload
                listType="text"
                fileList={[]}
                onChange={handleFileChange}
                beforeUpload={beforeUpload}
                multiple={true}
                showUploadList={false}
                customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
              >
                <Button 
                  icon={<PictureOutlined />} 
                  style={{ width: '120px', height: '32px' }}
                >
                  上传图片
                </Button>
              </Upload>
            </div>
          } 
          className="helppost-new-comment-card"
        >
          <div className="new-comment-form">
            {/* Display uploaded images above the text box */}
            {fileList.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {fileList.map((file, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img 
                        src={file.url || URL.createObjectURL(file.originFileObj)} 
                        alt={`Preview ${index}`} 
                        style={{ width: '100px', height: '100px', objectFit: 'cover' }} 
                      />
                      <div 
                        style={{ 
                          position: 'absolute', 
                          top: 5, 
                          right: 5,
                          background: 'rgba(230, 155, 155, 0.4)',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: '12px',
                          lineHeight: 1,
                          cursor: 'pointer',
                          color: '#ff4d4f'
                        }}
                        onClick={() => {
                          const newFileList = [...fileList];
                          newFileList.splice(index, 1);
                          setFileList(newFileList);
                        }}
                      >
                        X
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <TextArea
              rows={4}
              value={newComment}
              onChange={handleNewCommentChange}
              placeholder="输入您的回复..."
              style={{ marginBottom: '10px' }}
            />
            
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button 
                type="primary" 
                onClick={handleSubmitComment}
              >
                提交回复
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card title="添加回复" className="helppost-new-comment-card">
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p>请先登录后再发表回复</p>
            <Button 
              type="primary" 
              onClick={() => window.location.href = '/login'}
            >
              去登录
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default HelpPostDetail;
